import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from '@mui/material';
import { Assessment, Download, TableChart } from '@mui/icons-material';
import UtilisationReport from './reports/UtilisationReport';
import BookingLetters from './reports/BookingLetters';
import DailySchedulePrint from './reports/DailySchedulePrint';
import RoomUtilisationReport from './reports/RoomUtilisationReport';
import RoomCancellationsReport from './reports/RoomCancellationsReport';
import VccUtilisationReport from './reports/VccUtilisationReport';
import NursingChecksReport from './reports/NursingChecksReport';

// Registry of all reports — add new entries here as reports are built
const REPORTS = [
  {
    id: 'room-utilisation',
    label: 'Room Utilisation',
    description: 'Vacancy, reassignment and late-cancellation rates by specialty, location and session — with SPC run charts.',
    tags: ['Rooms', 'Specialty', 'SPC', 'CSV'],
    component: RoomUtilisationReport,
  },
  {
    id: 'room-cancellations',
    label: 'Room Cancellations',
    description: '≥4 weeks compliance, average notice days, and ad hoc reallocation of freed slots — by specialty, location and session.',
    tags: ['Cancellations', 'Compliance', 'CSV'],
    component: RoomCancellationsReport,
  },
  {
    id: 'nursing-checks',
    label: "Nurses' Check Submissions",
    description: 'Pivot table of daily check submission completeness by clinic — identify gaps and track trends.',
    tags: ['Nursing', 'Submissions', 'Pivot', 'CSV'],
    component: NursingChecksReport,
  },
  {
    id: 'vcc-utilisation',
    label: 'VCC Utilisation',
    description: 'Workstation allocation vs actual use — DNA and non-utilisation rates for the Virtual Call Centre.',
    tags: ['VCC', 'Workstations', 'CSV'],
    component: VccUtilisationReport,
  },
  {
    id: 'daily-schedule',
    label: 'Daily Room Schedule',
    description: 'Printable A4 landscape grid of all room bookings for a clinic on a given day — for updating whiteboards.',
    tags: ['Print', 'Daily', 'Rooms'],
    component: DailySchedulePrint,
  },
  {
    id: 'booking-letters',
    label: 'Monthly Booking Letters',
    description: 'Email each specialty their confirmed room bookings for the month as an Excel spreadsheet.',
    tags: ['Specialty', 'Email', 'Excel'],
    component: BookingLetters,
  },
  {
    id: 'utilisation',
    label: 'Room Utilisation vs Clinic Activity',
    description: 'Upload clinic session data to identify rooms booked but with no matching clinic activity.',
    tags: ['Rooms', 'Specialty', 'CSV'],
    component: UtilisationReport,
  },
];

function EmptyState() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 300,
        color: 'text.secondary',
        gap: 2,
      }}
    >
      <Assessment sx={{ fontSize: 64, opacity: 0.3 }} />
      <Typography variant="h6">Select a report to get started</Typography>
    </Box>
  );
}

function NoReports() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 300,
        color: 'text.secondary',
        gap: 2,
      }}
    >
      <TableChart sx={{ fontSize: 64, opacity: 0.3 }} />
      <Typography variant="h6">No reports yet</Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 360 }}>
        Reports will appear here as they are built. Each report will have filter
        options, an on-screen results table, and a CSV download.
      </Typography>
    </Box>
  );
}

export default function ReportingPage() {
  const [selectedReport, setSelectedReport] = useState(null);

  const selected = REPORTS.find(r => r.id === selectedReport);
  const ReportComponent = selected?.component || null;

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Reporting</Typography>
        <Typography variant="body2" color="text.secondary">
          Run and download reports from the room booking system.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* Sidebar — report list */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <Card>
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Available Reports
              </Typography>
              {REPORTS.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
                  No reports configured yet
                </Typography>
              ) : (
                REPORTS.map(report => (
                  <CardActionArea
                    key={report.id}
                    onClick={() => setSelectedReport(report.id)}
                    sx={{
                      borderRadius: 1,
                      p: 1.5,
                      mb: 0.5,
                      backgroundColor: selectedReport === report.id ? 'primary.light' : 'transparent',
                      '&:hover': { backgroundColor: selectedReport === report.id ? 'primary.light' : 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: selectedReport === report.id ? 'white' : 'text.primary' }}>
                      {report.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: selectedReport === report.id ? 'rgba(255,255,255,0.8)' : 'text.secondary', display: 'block', mt: 0.25 }}>
                      {report.description}
                    </Typography>
                    <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {report.tags?.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          icon={tag === 'CSV' ? <Download style={{ fontSize: 12 }} /> : undefined}
                          sx={{ fontSize: '0.6rem', height: 18 }}
                        />
                      ))}
                    </Box>
                  </CardActionArea>
                ))
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Main content — selected report */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Card sx={{ minHeight: 400 }}>
            <CardContent>
              {REPORTS.length === 0 ? (
                <NoReports />
              ) : !selected ? (
                <EmptyState />
              ) : (
                <ReportComponent />
              )}
            </CardContent>
          </Card>
        </Box>

      </Box>
    </Container>
  );
}
